import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ShieldAlert, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { apiGet, apiPost, ApiError } from '@/shared/api/client';
import { useAuth } from '@/shared/auth/AuthProvider';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input } from '@/components/ui/Input';

interface QuarantineRow {
  id: string;
  item_id: string | null;
  kit_instance_id: string | null;
  item_barcode: string | null;
  kit_barcode: string | null;
  reason: string;
  raised_at: string;
  raised_by_name: string | null;
  resolved_at: string | null;
  resolved_by_name: string | null;
  resolution: string | null;
}

export function QuarentenaPage() {
  const { user, loginPassword } = useAuth();
  const qc = useQueryClient();
  const list = useQuery({
    queryKey: ['quarantine', 'open'],
    queryFn: () => apiGet<{ items: QuarantineRow[] }>('/v1/quarantine?open=true'),
    refetchInterval: 30_000,
  });

  const canRelease = !!user && (user.role === 'ADMIN' || user.role === 'SUPERVISOR');

  const [target, setTarget] = useState<QuarantineRow | null>(null);
  const [resolution, setResolution] = useState<'RELEASED' | 'DISCARDED'>('RELEASED');
  const [notes, setNotes] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);

  const release = useMutation({
    mutationFn: async () => {
      if (!user) throw new Error('not_authenticated');
      // Re-auth obrigatório do supervisor antes de liberar
      await loginPassword(user.email, password, user.org_slug).catch(() => {
        throw new Error('Senha inválida — re-autenticação obrigatória');
      });
      return apiPost<{ id: string; resolved: true }>(
        '/v1/quarantine/release',
        { quarantine_id: target!.id, resolution, notes: notes || undefined },
        { idempotencyKey: `qrel-${target!.id}` },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['quarantine'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setTarget(null); setPassword(''); setNotes(''); setError(null);
    },
    onError: (e) => {
      if (e instanceof ApiError) setError(e.message);
      else setError((e as Error).message);
    },
  });

  return (
    <div className="space-y-4">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">
          Quarentena
        </h1>
        <p className="text-sm text-neutral-500">
          Itens e kits retidos por suspeita de não conformidade. Apenas Supervisor ou Admin liberam, com re-autenticação.
        </p>
      </header>

      <Card>
        <CardHeader
          title="Em quarentena"
          subtitle={list.isLoading ? 'Carregando…' : `${list.data?.items.length ?? 0} aberto(s)`}
        />
        <CardBody className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500 dark:bg-neutral-700">
              <tr>
                <th className="px-4 py-2">Alvo</th>
                <th className="px-4 py-2">Motivo</th>
                <th className="px-4 py-2">Aberto por</th>
                <th className="px-4 py-2">Quando</th>
                <th className="px-4 py-2 text-right">Ação</th>
              </tr>
            </thead>
            <tbody>
              {(list.data?.items ?? []).map((q) => (
                <tr key={q.id} className="border-t border-neutral-200 dark:border-neutral-700">
                  <td className="px-4 py-2">
                    <div className="flex items-center gap-2">
                      <ShieldAlert size={16} className="text-warning" />
                      <div className="min-w-0">
                        <div className="text-xs font-medium">{q.item_id ? 'Item' : 'Kit'}</div>
                        <div className="font-mono text-xxs text-neutral-500">{q.item_barcode ?? q.kit_barcode}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-2 text-xs">{q.reason}</td>
                  <td className="px-4 py-2 text-xs">{q.raised_by_name ?? '—'}</td>
                  <td className="px-4 py-2 font-mono text-xxs text-neutral-500">{q.raised_at}</td>
                  <td className="px-4 py-2 text-right">
                    <Button
                      variant="secondary"
                      size="sm"
                      leadingIcon={canRelease ? <CheckCircle2 size={14} /> : <Lock size={14} />}
                      disabled={!canRelease}
                      onClick={() => { setTarget(q); setError(null); setResolution('RELEASED'); }}
                    >
                      {canRelease ? 'Resolver' : 'Bloqueado'}
                    </Button>
                  </td>
                </tr>
              ))}
              {!list.isLoading && (list.data?.items?.length ?? 0) === 0 ? (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-xs text-neutral-500">Nenhum item em quarentena.</td></tr>
              ) : null}
            </tbody>
          </table>
        </CardBody>
      </Card>

      {target ? (
        <div className="fixed inset-0 z-50 grid place-items-center bg-neutral-900/50 p-4">
          <div className="w-full max-w-md rounded border border-neutral-200 bg-neutral-0 shadow-md dark:bg-neutral-800 dark:border-neutral-700">
            <div className="border-b border-neutral-200 px-4 py-3 dark:border-neutral-700">
              <h2 className="text-md font-semibold">Resolver quarentena</h2>
              <p className="text-xs text-neutral-500">
                {target.item_id ? 'Item' : 'Kit'} <span className="font-mono">{target.item_barcode ?? target.kit_barcode}</span> — {target.reason}
              </p>
            </div>
            <div className="space-y-3 p-4">
              <div className="flex gap-2">
                <Button
                  variant={resolution === 'RELEASED' ? 'primary' : 'secondary'}
                  size="md"
                  block
                  leadingIcon={<CheckCircle2 size={16} />}
                  onClick={() => setResolution('RELEASED')}
                >
                  Liberar
                </Button>
                <Button
                  variant={resolution === 'DISCARDED' ? 'danger' : 'secondary'}
                  size="md"
                  block
                  leadingIcon={<XCircle size={16} />}
                  onClick={() => setResolution('DISCARDED')}
                >
                  Descartar
                </Button>
              </div>
              <Input label="Observações (opcional)" value={notes} onChange={(e) => setNotes(e.target.value)} />
              <Input
                label="Confirme sua senha"
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                hint={`Re-autenticação obrigatória para ${user?.email ?? ''}`}
                error={error ?? undefined}
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t border-neutral-200 px-4 py-3 dark:border-neutral-700">
              <Button variant="ghost" size="md" onClick={() => { setTarget(null); setPassword(''); setNotes(''); setError(null); }}>
                Cancelar
              </Button>
              <Button
                variant={resolution === 'DISCARDED' ? 'danger' : 'primary'}
                size="md"
                loading={release.isPending}
                disabled={!password}
                onClick={() => release.mutate()}
              >
                Confirmar {resolution === 'RELEASED' ? 'liberação' : 'descarte'}
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
